import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const DISMISS_KEY = 'pubgolf.installDismissed';

/**
 * Detect iOS Safari specifically — Chrome/Firefox on iOS are all WebKit
 * but only Mobile Safari supports Add to Home Screen from the Share sheet.
 */
function detectIosSafari() {
  if (typeof navigator === 'undefined') {
    return { isIos: false, isIosSafari: false, isStandalone: false };
  }
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // "CriOS" = Chrome iOS, "FxiOS" = Firefox iOS, "EdgiOS" = Edge iOS.
  const isIosSafari =
    isIos && /Safari/.test(ua) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/.test(ua);
  // iOS standalone flag (only set when launched from home screen).
  const isStandalone =
    window.navigator.standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true;
  return { isIos, isIosSafari, isStandalone };
}

/** Small SVG of the iOS Share icon (square with an up-arrow). */
function ShareIcon({ size = 18 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

export default function InstallPrompt() {
  const loc = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1'
  );
  const [iosOpen, setIosOpen] = useState(false);
  const [platform, setPlatform] = useState({
    isIos: false,
    isIosSafari: false,
    isStandalone: false,
  });

  // Don't show on login or in-game screens.
  const hideOnRoute =
    loc.pathname.startsWith('/login') ||
    loc.pathname.startsWith('/game/') ||
    loc.pathname.startsWith('/setup');

  useEffect(() => {
    // Android / desktop Chromium path — listen for the prompt event.
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS path — detect once on mount.
    setPlatform(detectIosSafari());

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (dismissed || hideOnRoute) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') dismiss();
    } catch {}
    setDeferred(null);
  };

  // Android/desktop — native prompt captured, show the one-tap install banner.
  if (deferred) {
    return (
      <div className="install-banner">
        <div className="install-banner-text">
          <strong>Install Pub Golf</strong>
          <span>Add it to your home screen for the full experience</span>
        </div>
        <div className="install-banner-actions">
          <button className="btn btn-ghost" onClick={dismiss}>
            Later
          </button>
          <button className="btn btn-primary" onClick={install}>
            Install
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari — show a hint chip that expands into a full instructions sheet.
  if (platform.isIosSafari && !platform.isStandalone) {
    return (
      <>
        <button
          className="install-banner install-banner-ios"
          onClick={() => setIosOpen(true)}
          aria-label="Install Pub Golf on iOS"
        >
          <span className="install-banner-icon" aria-hidden="true">
            <ShareIcon size={18} />
          </span>
          <div className="install-banner-text">
            <strong>Add Pub Golf to your Home Screen</strong>
            <span>Tap Share, then "Add to Home Screen"</span>
          </div>
          <span className="install-banner-chev">›</span>
        </button>

        {iosOpen && (
          <div
            className="ios-install-overlay"
            onClick={() => setIosOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="ios-install-sheet"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ios-install-handle" />
              <h2 className="ios-install-title">Install Pub Golf</h2>
              <p className="ios-install-sub">
                Get the full app experience on your iPhone — no App Store
                required.
              </p>

              <ol className="ios-install-steps">
                <li>
                  <span className="ios-install-step-num">1</span>
                  <div className="ios-install-step-body">
                    <div className="ios-install-step-text">
                      Tap the <strong>Share</strong> button at the bottom of
                      Safari
                    </div>
                    <div className="ios-install-step-icon">
                      <ShareIcon size={22} />
                    </div>
                  </div>
                </li>
                <li>
                  <span className="ios-install-step-num">2</span>
                  <div className="ios-install-step-body">
                    <div className="ios-install-step-text">
                      Scroll down and tap{' '}
                      <strong>Add to Home Screen</strong>
                    </div>
                    <div className="ios-install-step-icon" aria-hidden="true">
                      <span className="ios-plus-icon">＋</span>
                    </div>
                  </div>
                </li>
                <li>
                  <span className="ios-install-step-num">3</span>
                  <div className="ios-install-step-body">
                    <div className="ios-install-step-text">
                      Tap <strong>Add</strong> in the top-right — you're done.
                    </div>
                  </div>
                </li>
              </ol>

              <div className="ios-install-actions">
                <button className="btn btn-primary btn-block" onClick={dismiss}>
                  Got it
                </button>
                <button
                  className="btn btn-ghost btn-block"
                  onClick={() => setIosOpen(false)}
                >
                  Remind me later
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return null;
}
